import * as React from "react";
import { cn } from "@uconnect/assets/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import Icon from "@uconnect/assets/components/ui/icon";
type SearchInputProps = React.ComponentProps<typeof Input> & {
	iconClassName?: string;
	wrapperClassName?: string;
	/**
	 * Shows a clear ("x") button while the field has a value, and lets Escape clear it too. Needs a
	 * controlled `value`: the button only appears when `value` isn't empty.
	 */
	onClear?: () => void;
	/** Accessible name for the clear button. */
	clearLabel?: string;
};

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
	({ iconClassName, wrapperClassName, onClear, clearLabel = "Clear search", onKeyDown, ...props }, ref) => {
		const inputRef = React.useRef<HTMLInputElement>(null);
		React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

		const canClear = Boolean(onClear) && String(props.value ?? "") !== "";

		// Focus goes back to the field: the button disappears once the value is empty, and focus would
		// otherwise fall back to the top of the page.
		const clear = () => {
			onClear?.();
			inputRef.current?.focus();
		};

		return (
			<div className={cn("relative", wrapperClassName)}>
				<Input
					{...props}
					ref={inputRef}
					onKeyDown={(event) => {
						if (canClear && event.key === "Escape") {
							event.preventDefault();
							event.stopPropagation();
							clear();
						}
						onKeyDown?.(event);
					}}
					className={cn("!pl-8", onClear && "!pr-8", props.className)}
				/>
				<Icon
					name="search"
					className={cn('absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground leading-1 w-4 h-4 z-10', iconClassName)}
					aria-hidden="true"
				/>
				{canClear && (
					<button
						type="button"
						onClick={clear}
						aria-label={clearLabel}
						className={cn(
							"absolute right-1 top-1/2 -translate-y-1/2 z-10 inline-flex size-6 items-center justify-center",
							"rounded-sm text-muted-foreground hover:text-foreground cursor-pointer",
						)}
					>
						<Icon name="xmark" className="size-3.5" aria-hidden="true" />
					</button>
				)}
			</div>
		);
	}
);
SearchInput.displayName = "SearchInput";

const inputVariants = cva(
	"file:text-foreground placeholder:text-muted-foreground! selection:bg-primary selection:text-primary-foreground w-full min-w-0 !rounded-md border transition-[color] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive !min-h-0",
	{
		variants: {
			variant: {
				default: "!border-border bg-white",
				subtle: "border-none bg-muted/40",
			},
			size: {
				default: "h-9 !text-base !px-3 !py-1",
				sm: "h-8 !text-sm !px-2 !py-1",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

type InputProps = Omit<React.ComponentProps<"input">, "size"> &
	VariantProps<typeof inputVariants> & {
		className?: string;
	};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, type, variant, size, ...props }, ref) => {
		return (
			<input
				type={type}
				data-slot="input"
				ref={ref}
				className={cn(inputVariants({ variant, size }), className)}
				{...props}
			/>
		);
	}
);
Input.displayName = "Input";

export { Input, SearchInput };
